import { useState, useEffect, useMemo, useCallback } from 'react';
import { MapPin, Droplets, Gauge, AlertTriangle, Activity, TrendingUp, Zap, Calendar, RefreshCw, Settings, Plus, Sparkles, Bell, Clock, BarChart3, Users } from 'lucide-react';
import { toast } from 'sonner';

// Child components
import { StatGrid } from './dashboard/StatGrid';
import { FieldStatusWidget } from './dashboard/FieldStatusWidget';
import { QuickStatsRow } from './dashboard/QuickStatsRow';
import { QuickActions } from './dashboard/QuickActions';
import { TopProducers } from './dashboard/TopProducers';
import { OverdueWellsList } from './dashboard/OverdueWellsList';
import { RecentActivity } from './dashboard/RecentActivity';
import { SystemAlerts } from './dashboard/SystemAlerts';

// Production calculations
import { useProductionCalculator } from '../hooks/useProductionCalculator';

interface DashboardProps {
  wells: any[];
  overdueWells: any[];
  onSelectWell: (well: any) => void;
  onAddData: () => void;
  onShowVoiceAssistant?: () => void;
  onNavigate: (screen: string) => void;
  user?: any;
  isDarkMode?: boolean;
  onToggleTheme?: () => void;
}

export default function Dashboard({
  wells = [],
  overdueWells = [],
  onSelectWell,
  onAddData,
  onShowVoiceAssistant,
  onNavigate,
  user,
  isDarkMode = false,
  onToggleTheme = () => { }
}: DashboardProps) {

  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Production calculator hook
  const { calculateProduction, inchesToBarrels } = useProductionCalculator();
  
  // Calculate production for all wells
  const calculatedProductionData = useMemo(() => {
    const results = new Map<string, {
      netOilBBL: number;
      grossWaterBBL: number;
      waterCutPercent: number;
      oilVsYesterdayPercent?: number;
    }>();
    
    wells.forEach(well => {
      // Try to use latest entry data if available
      if (well.latestEntry || well.tankGauges) {
        try {
          const tankData = well.latestEntry?.tankGauges || well.tankGauges;
          if (tankData) {
            // Build tank readings from well data
            const oilTanks = (tankData.oil || []).map((t: any, i: number) => ({
              tankNumber: i + 1,
              currentLevelInches: t.topGauge || t.inches || 0,
              previousLevelInches: t.previousTopGauge || t.topGauge || 0,
              bblPerInch: t.bblPerInch || well.tanks?.find((tank: any) => tank.type === 'oil')?.bblPerInch || 3.57,
              tankType: 'oil' as const,
              kolorKutWaterInches: t.kolorKut?.waterCutInches || 0,
            }));
            
            const waterTanks = (tankData.water || []).map((t: any, i: number) => ({
              tankNumber: i + 1,
              currentLevelInches: t.topGauge || t.inches || 0,
              previousLevelInches: t.previousTopGauge || t.topGauge || 0,
              bblPerInch: t.bblPerInch || well.tanks?.find((tank: any) => tank.type === 'water')?.bblPerInch || 3.57,
              tankType: 'water' as const,
            }));
            
            if (oilTanks.length > 0 || waterTanks.length > 0) {
              const result = calculateProduction({
                wellId: well.id,
                wellName: well.name,
                entryDate: new Date().toISOString().split('T')[0],
                oilTanks,
                waterTanks,
                barrelsHauled: well.latestEntry?.barrelsHauled || 0,
              });
              
              results.set(well.id, {
                netOilBBL: result.netOilBBL,
                grossWaterBBL: result.grossWaterBBL,
                waterCutPercent: result.waterCutPercent,
                oilVsYesterdayPercent: 0, // Would need historical data
              });
            }
          }
        } catch (err) {
        }
      }
      
      // Fall back to raw values if no calculation possible
      if (!results.has(well.id) && (well.oilProduction || well.waterProduction)) {
        results.set(well.id, {
          netOilBBL: well.oilProduction || 0,
          grossWaterBBL: well.waterProduction || 0,
          waterCutPercent: well.waterCutPercent || 0,
        });
      }
    });
    
    return results;
  }, [wells, calculateProduction]);

  // Silently mark certain declared values as read to satisfy TS 6133
  void useMemo; void useCallback; void inchesToBarrels;
  void RefreshCw; void Settings; void Plus; void Sparkles;
  void onToggleTheme;
  void lastRefresh; void setAutoRefresh;

  // Calculate metrics using calculated production when available
  const activeWellsCount = wells.filter(w => w.status === 'active').length;
  
  // Use calculated values for totals
  const totalOil = useMemo(() => {
    let sum = 0;
    calculatedProductionData.forEach(data => {
      sum += data.netOilBBL || 0;
    });
    // Fall back to raw values if no calculated data
    if (sum === 0) {
      sum = wells.reduce((s, w) => s + (Number(w.oilProduction) || 0), 0);
    }
    return sum;
  }, [calculatedProductionData, wells]);
  
  const totalWater = useMemo(() => {
    let sum = 0;
    calculatedProductionData.forEach(data => {
      sum += data.grossWaterBBL || 0;
    });
    // Fall back to raw values if no calculated data
    if (sum === 0) {
      sum = wells.reduce((s, w) => s + (Number(w.waterProduction) || 0), 0);
    }
    return sum;
  }, [calculatedProductionData, wells]);
  
  const overdueCount = overdueWells.length;
  const totalProduction = totalOil + totalWater;

  // Calculate additional metrics
  const recentEntries = wells.filter(w => w.lastEntry && new Date(w.lastEntry).getTime() > Date.now() - 24 * 60 * 60 * 1000).length;

  void totalProduction; // computed metrics, unused in direct render but good for tracking

  // Get top producers
  const topProducers = [...wells]
    .filter(w => w.oilProduction && w.oilProduction > 0)
    .sort((a, b) => (b.oilProduction || 0) - (a.oilProduction || 0))
    .slice(0, 3);

  // Find next check
  const nextCheckWell = wells.find(w => !w.lastEntry || new Date(w.lastEntry).getDate() !== new Date().getDate());

  // Find previous well
  const previousWell = [...wells]
    .filter(w => w.lastEntry)
    .sort((a, b) => new Date(b.lastEntry).getTime() - new Date(a.lastEntry).getTime())[0];

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      setLastRefresh(new Date());
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  // Determine Field Status
  const getFieldStatus = () => {
    if (overdueCount > 0) {
      return {
        title: 'Attention Required',
        message: `${overdueCount} wells require immediate attention`,
        color: 'orange',
        icon: AlertTriangle
      };
    }
    if (activeWellsCount < wells.length) {
      return {
        title: 'Status Update',
        message: `${wells.length - activeWellsCount} wells are currently inactive`,
        color: 'blue',
        icon: Activity
      };
    }
    return {
      title: 'All Systems Active',
      message: 'No wells require immediate attention',
      color: 'emerald',
      icon: Activity
    };
  };

  const fieldStatus = getFieldStatus();

  const metrics = [
    {
      id: 'total-wells',
      label: 'Total Wells',
      value: wells.length,
      subtext: `${activeWellsCount} active`,
      icon: MapPin,
      variant: 'primary' as const
    },
    {
      id: 'need-check',
      label: 'Need Check',
      value: overdueCount,
      subtext: 'overdue',
      icon: AlertTriangle,
      variant: 'critical' as const
    },
    {
      id: 'oil-prod',
      label: 'Oil Production',
      value: totalOil.toFixed(0),
      subtext: 'BPD total',
      icon: Droplets,
      variant: 'success' as const
    },
    {
      id: 'water-prod',
      label: 'Water Prod.',
      value: totalWater.toFixed(0),
      subtext: 'BPD total',
      icon: Gauge,
      variant: 'entity' as const
    },
  ];

  const handleCardClick = (id: string) => {
    switch (id) {
      case 'total-wells':
        onNavigate('well-view-listings?filter=all');
        break;
      case 'need-check':
        onNavigate('well-view-listings?filter=overdue');
        break;
      case 'oil-prod':
        onNavigate('well-view-listings?sort=oil');
        break;
      case 'water-prod':
        onNavigate('well-view-listings?sort=water');
        break;
    }
  };

  const quickStats = [
    {
      id: 'daily-avg',
      icon: TrendingUp,
      label: 'Daily Average',
      value: `${(totalOil / (activeWellsCount || 1)).toFixed(1)} BPD`,
      gradient: 'from-cyan-500 via-cyan-600 to-blue-500',
      iconColor: 'text-cyan-600',
      bgGradient: 'from-cyan-400/20 via-cyan-500/20 to-blue-400/20'
    },
    {
      id: 'prev-well',
      icon: Zap,
      label: 'Previous Well',
      value: previousWell ? (previousWell.name.length > 10 ? previousWell.name.substring(0, 10) + '...' : previousWell.name) : 'None',
      gradient: 'from-amber-500 via-amber-600 to-yellow-500',
      iconColor: 'text-amber-600',
      bgGradient: 'from-amber-400/20 via-amber-500/20 to-yellow-400/20'
    },
    {
      id: 'next-check',
      icon: Calendar,
      label: 'Next Check',
      value: nextCheckWell ? (nextCheckWell.name.length > 10 ? nextCheckWell.name.substring(0, 10) + '...' : nextCheckWell.name) : 'None',
      gradient: 'from-rose-500 via-rose-600 to-pink-500',
      iconColor: 'text-rose-600',
      bgGradient: 'from-rose-400/20 via-rose-500/20 to-pink-400/20'
    },
  ];

  const handleQuickStatClick = (id: string) => {
    switch (id) {
      case 'daily-avg':
        onNavigate('analytics');
        break;
      case 'prev-well':
        if (previousWell) {
          onSelectWell(previousWell);
        } else {
          toast.info('No previous well found');
        }
        break;
      case 'next-check':
        if (nextCheckWell) {
          onSelectWell(nextCheckWell);
        } else {
          toast.info('All wells checked today!');
        }
        break;
    }
  };

  const handleFieldStatusClick = () => {
    if (overdueCount > 0) {
      onNavigate('well-view-listings?filter=overdue');
    } else {
      onNavigate('well-view-listings?filter=active');
    }
  };

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'add-entry':
        onAddData();
        break;
      case 'check-overdue':
        if (overdueWells.length > 0) {
          onSelectWell(overdueWells[0]);
        }
        break;
      case 'voice-assistant':
        if (onShowVoiceAssistant) {
          onShowVoiceAssistant();
        }
        break;
      case 'analytics':
        onNavigate('analytics');
        break;
      case 'tickets':
        onNavigate('tickets');
        break;
      case 'settings':
        onNavigate('settings');
        break;
    }
  };

  return (
    <div className="px-4 py-6">
      {/* Header with controls */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {user?.name ? `${user.name}'s Dashboard` : 'Dashboard'}
          </h2>
          <div className={`px-2 py-1 rounded-full text-xs ${autoRefresh ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
            {autoRefresh ? 'Live' : 'Static'}
          </div>
        </div>
      </div>

      {/* Main Metrics Grid */}
      <StatGrid metrics={metrics} isDarkMode={isDarkMode} onCardClick={handleCardClick} />

      {/* Field Status */}
      <FieldStatusWidget 
        status={fieldStatus} 
        isDarkMode={isDarkMode} 
        onClick={handleFieldStatusClick}
      />

      {/* Quick Stats Row */}
      <QuickStatsRow 
        stats={quickStats} 
        isDarkMode={isDarkMode} 
        onStatClick={handleQuickStatClick}
      />

      {/* Quick Actions */}
      <QuickActions onAction={handleQuickAction} overdueCount={overdueCount} />

      {/* Top Producers & Overdue Wells */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <TopProducers 
          wells={wells} 
          isDarkMode={isDarkMode} 
          onSelectWell={onSelectWell}
          calculatedProduction={calculatedProductionData}
          useCalculatedValues={true}
        />
        <OverdueWellsList wells={overdueWells} isDarkMode={isDarkMode} onSelectWell={onSelectWell} />
      </div>

      {/* Recent Activity & Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <RecentActivity recentEntries={recentEntries} activeWellsCount={activeWellsCount} isDarkMode={isDarkMode} />
        <SystemAlerts
          overdueCount={overdueCount}
          activeWellsCount={activeWellsCount}
          totalWells={wells.length}
          totalOil={totalOil}
          isDarkMode={isDarkMode}
        />
      </div>
    </div>
  );
}
